import { HttpParams } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseModal, ListItem, VpJob } from '../../shared'
import { WorkOrderQueueComponent } from './work-order-queue.component'

interface EquipmentSkillCategoryDto {
    code: string
    name: string
}

interface MechanicBySkillDto {
    hrRef: number
    displayName: string
}

interface OpenWebAppsWorkOrder {
    id: string
    workOrderNumber: string
    description: string
    equipment: string
    job: string
}

interface VpEquipmentRow {
    equipment: string
    description: string
}

@Component({
    templateUrl: './work-order-manual.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent],
})
export class WorkOrderManualComponent extends BaseModal implements OnInit {
    faSpinner = faSpinner
    parentComponent!: WorkOrderQueueComponent

    mode: 'create' | 'addExisting' = 'create'
    equipment = ''
    job = ''
    description = ''
    systemAffected = ''
    notes = ''
    assignedMechanicHrRef: number | null = null
    selectedWorkOrderId = ''

    equipmentOptions: ListItem<string>[] = []
    jobOptions: ListItem<string>[] = []
    skillCategoryOptions: ListItem<string>[] = [new ListItem('', 'Select...')]
    mechanicOptions: ListItem<number | null>[] = [new ListItem<number | null>(null, 'Unassigned')]
    openWorkOrderOptions: ListItem<string>[] = [new ListItem('', 'Select an open work order...')]

    private equipmentByCode = new Map<string, VpEquipmentRow>()

    readonly title = 'Work Order'

    ngOnInit() {
        this.loadLookups()
    }

    get isCreateMode() {
        return this.mode === 'create'
    }

    onModeChanged() {
        this.selectedWorkOrderId = ''
        this.openWorkOrderOptions = [new ListItem('', 'Select an open work order...')]
        if (!this.isCreateMode && this.equipment)
            this.loadOpenWorkOrders(this.equipment)
    }

    setMode(nextMode: 'create' | 'addExisting') {
        this.mode = nextMode
        this.onModeChanged()
    }

    onEquipmentChanged() {
        if (this.isCreateMode && this.equipment)
            this.loadEquipmentJob(this.equipment)

        if (!this.isCreateMode)
            this.loadOpenWorkOrders(this.equipment)
    }

    onSystemAffectedChanged() {
        if (!this.systemAffected) {
            this.mechanicOptions = [new ListItem<number | null>(null, 'Unassigned')]
            this.assignedMechanicHrRef = null
            return
        }

        this.services.data
            .get<MechanicBySkillDto[]>(`api/EquipmentTechRoleCapability/mechanics-by-skill/${encodeURIComponent(this.systemAffected)}`)
            .subscribe({
                next: mechanics => {
                    this.mechanicOptions = [
                        new ListItem<number | null>(null, 'Unassigned'),
                        ...mechanics.map(m => new ListItem<number | null>(m.hrRef, m.displayName)),
                    ]
                    if (
                        this.assignedMechanicHrRef != null &&
                        !mechanics.some(m => m.hrRef === this.assignedMechanicHrRef)
                    ) {
                        this.assignedMechanicHrRef = null
                    }
                },
                error: () => {
                    this.services.notification.error('Failed to load mechanics for the selected system.')
                },
            })
    }

    protected onBeforeSave(): boolean {
        if (!this.equipment) {
            this.services.notification.error('Equipment is required.')
            return false
        }

        if (!this.description?.trim()) {
            this.services.notification.error('Description is required.')
            return false
        }

        if (this.isCreateMode) {
            if (!this.systemAffected) {
                this.services.notification.error('System affected is required.')
                return false
            }
        } else if (!this.selectedWorkOrderId) {
            this.services.notification.error('Select an open WebApps work order.')
            return false
        }

        return true
    }

    protected onSave(resolveSave: (result?: unknown) => void, rejectSave: (reason?: unknown) => void) {
        if (this.isCreateMode) {
            const body = {
                equipment: this.equipment,
                job: this.job || undefined,
                description: this.description.trim(),
                systemAffected: this.systemAffected,
                notes: this.notes?.trim() || undefined,
                assignedMechanicHrRef: this.assignedMechanicHrRef ?? undefined,
            }

            this.services.data.post('api/EquipmentWorkOrder/manual', body).subscribe({
                next: () => resolveSave(),
                error: errorResult => rejectSave(errorResult),
            })
            return
        }

        const body = {
            description: this.description.trim(),
            notes: this.notes?.trim() || undefined,
        }

        this.services.data.post(`api/EquipmentWorkOrder/${this.selectedWorkOrderId}/items`, body).subscribe({
            next: () => resolveSave(),
            error: errorResult => rejectSave(errorResult),
        })
    }

    private loadLookups() {
        const skillParams = new HttpParams().set('activeOnly', 'true')
        this.services.data
            .load(
                false,
                false,
                this.services.data.get<VpEquipmentRow[]>('api/VpEquipment'),
                this.services.data.get<VpJob[]>('api/VpJob'),
                this.services.data.get<EquipmentSkillCategoryDto[]>('api/EquipmentSkillCategory', skillParams),
            )
            .subscribe({
                next: result => {
                    const equipment = (result[0] as VpEquipmentRow[]).orderBy(x => x.equipment)
                    this.equipmentByCode.clear()
                    for (const row of equipment)
                        this.equipmentByCode.set(row.equipment, row)

                    this.equipmentOptions = [
                        new ListItem('', 'Select equipment...'),
                        ...equipment.map(x => new ListItem(x.equipment, `${x.equipment}: ${x.description}`)),
                    ]

                    const jobs = result[1] as VpJob[]
                    this.jobOptions = [
                        new ListItem('', 'Select a job...'),
                        ...jobs
                            .map(job => new ListItem(job.id, `${job.id} ${job.name}`))
                            .sort((a, b) => a.display.localeCompare(b.display)),
                    ]

                    const categories = result[2] as EquipmentSkillCategoryDto[]
                    this.skillCategoryOptions = [
                        new ListItem('', 'Select...'),
                        ...categories.map(c => new ListItem(c.code, c.name)),
                    ]
                },
                error: () => {
                    this.services.notification.error('Failed to load work order form data.')
                },
            })
    }

    private loadEquipmentJob(equipmentCode: string) {
        const params = new HttpParams().set('equipment', equipmentCode)
        this.services.data.get<{ job?: string }>('api/EquipmentWorkOrder/equipment-job', params).subscribe({
            next: result => {
                this.job = result?.job ?? ''
            },
            error: () => {
                this.job = ''
            },
        })
    }

    private loadOpenWorkOrders(equipmentCode: string) {
        if (!equipmentCode) {
            this.openWorkOrderOptions = [new ListItem('', 'Select an open work order...')]
            this.selectedWorkOrderId = ''
            return
        }

        const params = new HttpParams().set('equipment', equipmentCode).set('openOnly', 'true')
        this.services.data.get<OpenWebAppsWorkOrder[]>('api/EquipmentWorkOrder', params).subscribe({
            next: workOrders => {
                this.openWorkOrderOptions =
                    workOrders.length === 0
                        ? [new ListItem('', 'No open WebApps work orders for this equipment')]
                        : [
                              new ListItem('', 'Select an open work order...'),
                              ...workOrders.map(wo =>
                                  new ListItem(wo.id, `${wo.workOrderNumber} — ${wo.description ?? ''}`.trim()),
                              ),
                          ]
                this.selectedWorkOrderId = ''
            },
            error: () => {
                this.services.notification.error('Failed to load open work orders.')
            },
        })
    }
}
